import { useQueryClient } from "@tanstack/react-query";

import { usePatchUserPreferencesMutation } from "@shared/api/server/gs3Server";
import type { User } from "@shared/api/server/gs3Server";
import { clearPendingLocale, savePendingLocale } from "@shared/utils";

import { shouldReloadPageOnGoogleMapsRoute } from "../utils/shouldReloadPageOnGoogleMapsRoute";
import { useLocalization } from "./useLocalization";

const onMapsRoute = () =>
  typeof window !== "undefined" &&
  shouldReloadPageOnGoogleMapsRoute(window.location.pathname);

export const usePersistLanguage = () => {
  const queryClient = useQueryClient();
  const { changeLanguage, language } = useLocalization();
  const { mutateAsync } = usePatchUserPreferencesMutation();

  return async (languageCode: string) => {
    const me = queryClient.getQueryData<User>(["me"]);
    if (!me) {
      savePendingLocale(languageCode);
      await changeLanguage(languageCode);
      return;
    }

    const mapsRoute = onMapsRoute();
    await changeLanguage(languageCode, { reload: false });
    try {
      await mutateAsync({ language: languageCode });
    } catch {
      await changeLanguage(language, { reload: false });
      return;
    }

    clearPendingLocale();

    if (mapsRoute && languageCode !== language) {
      window.location.reload();
    }
  };
};
